// Step 3: Pricing - group, reduced group, private pricing, and commission settings
// At least one price required for published tours; all optional for concepts
// Updated: Added commission settings (type + value) for per-tour commission tracking

import React from 'react';
import { DollarSign, Percent, User } from 'lucide-react';
import { Tour } from '../../../types/tours';

interface StepPricingProps {
  formData: Partial<Tour>;
  isConcept: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectChange: (field: string, value: string) => void;
}

const StepPricing: React.FC<StepPricingProps> = ({ formData, isConcept, onChange, onSelectChange }) => {
  const commissionType = formData.commission_type || 'percentage';

  return (
    <div className="space-y-6">
      <div className="bg-[#1F2B1F] rounded-xl p-6 border border-white/10">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Pricing</h3>
          <p className="text-sm text-white/40">
            {isConcept ? 'All pricing is optional for concepts' : 'At least one price option is required'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/5 rounded-lg p-5 border border-white/10">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-[#E85C0D]" />
              <h4 className="font-medium text-white">Group Tour</h4>
            </div>
            <p className="text-xs text-white/40 mb-3">Maximum 10 persons</p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
              <input
                type="number"
                id="group_tour_price"
                name="group_tour_price"
                value={formData.group_tour_price || ''}
                onChange={onChange}
                min="0"
                step="0.01"
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-3 bg-white/5 border border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white placeholder-white/20 transition-all"
              />
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-5 border border-white/10">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-[#E85C0D]" />
              <h4 className="font-medium text-white">Reduced Group</h4>
            </div>
            <p className="text-xs text-white/40 mb-3">Maximum 4 persons</p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
              <input
                type="number"
                id="reduced_group_price"
                name="reduced_group_price"
                value={formData.reduced_group_price || ''}
                onChange={onChange}
                min="0"
                step="0.01"
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-3 bg-white/5 border border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white placeholder-white/20 transition-all"
              />
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-5 border border-white/10">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="w-5 h-5 text-[#E85C0D]" />
              <h4 className="font-medium text-white">Private Tour</h4>
            </div>
            <p className="text-xs text-white/40 mb-3">Private tour for your group only</p>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
              <input
                type="number"
                id="private_price"
                name="private_price"
                value={formData.private_price || ''}
                onChange={onChange}
                min="0"
                step="0.01"
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-3 bg-white/5 border border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white placeholder-white/20 transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#1F2B1F] rounded-xl p-6 border border-white/10">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Commission</h3>
          <p className="text-sm text-white/40">How the platform earns from this tour</p>
        </div>

        <div className="bg-white/5 rounded-lg p-5 border border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-3">Commission Type</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => onSelectChange('commission_type', 'percentage')}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                    commissionType === 'percentage'
                      ? 'bg-[#E85C0D]/20 border-[#E85C0D] text-white'
                      : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'
                  }`}
                >
                  <Percent className="w-4 h-4" />
                  <span className="text-sm font-medium">Percentage</span>
                </button>
                <button
                  type="button"
                  onClick={() => onSelectChange('commission_type', 'per_person')}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                    commissionType === 'per_person'
                      ? 'bg-[#E85C0D]/20 border-[#E85C0D] text-white'
                      : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'
                  }`}
                >
                  <User className="w-4 h-4" />
                  <span className="text-sm font-medium">Per Person</span>
                </button>
              </div>
              <p className="text-xs text-white/40 mt-3">
                {commissionType === 'percentage'
                  ? 'Platform takes a percentage of the total booking price.'
                  : 'Operator earns a fixed amount per person; platform takes 8.5% of that amount.'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-3">
                {commissionType === 'percentage' ? 'Commission Rate (%)' : 'Amount per Person ($)'}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">
                  {commissionType === 'percentage' ? '%' : '$'}
                </span>
                <input
                  type="number"
                  id="commission_value"
                  name="commission_value"
                  value={commissionType === 'percentage'
                    ? (formData.commission_value != null ? (formData.commission_value * 100) : '')
                    : (formData.commission_value ?? '')
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      onChange({ ...e, target: { ...e.target, name: 'commission_value', value: '' } } as any);
                      return;
                    }
                    const num = Number(raw);
                    const stored = commissionType === 'percentage' ? num / 100 : num;
                    onChange({ ...e, target: { ...e.target, name: 'commission_value', value: String(stored) } } as any);
                  }}
                  min="0"
                  step={commissionType === 'percentage' ? '0.1' : '0.01'}
                  placeholder={commissionType === 'percentage' ? '8.5' : '15.00'}
                  className="w-full pl-8 pr-4 py-3 bg-white/5 border border-white/15 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white placeholder-white/20 transition-all"
                />
              </div>
              <p className="text-xs text-white/40 mt-3">
                {commissionType === 'percentage'
                  ? 'Enter the percentage of the total booking price the platform earns.'
                  : 'Enter the dollar amount earned per person. Platform takes 8.5% of this.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepPricing;
